import { useMemo } from 'react';
import type { TickerResult } from '@/components/tables/ReturnsTable';
import {
  type GroupingMode,
  type GroupedTickerData,
  type GroupAggregates,
  getPerformanceTier,
  PERFORMANCE_TIERS,
} from '@/types/grouping';

/**
 * Get the grouping key for a ticker based on the grouping mode
 */
function getGroupKey(ticker: TickerResult, mode: GroupingMode): string {
  switch (mode) {
    case 'ticker':
      return ticker.ticker;
    case 'underlying':
      return ticker.underlyingTicker || 'No Underlying';
    case 'sector':
      return ticker.sector || 'Unknown Sector';
    case 'industry':
      return ticker.industry || 'Unknown Industry';
    case 'assetType':
      return ticker.assetType || 'Unknown';
    case 'dividend':
      return ticker.dividendYield && ticker.dividendYield > 0
        ? 'Dividend Payers'
        : 'Non-Dividend Payers';
    case 'performance':
      return getPerformanceTier(ticker.cagr);
    default:
      return ticker.ticker;
  }
}

/**
 * Get a human-readable label for a group key
 */
function getGroupLabel(key: string, mode: GroupingMode): string {
  if (mode === 'performance') {
    const tier = PERFORMANCE_TIERS[key as keyof typeof PERFORMANCE_TIERS];
    return tier?.label || key;
  }
  return key;
}

/**
 * Calculate aggregate statistics for a group of tickers
 */
function calculateAggregates(tickers: TickerResult[]): GroupAggregates {
  if (tickers.length === 0) {
    return {
      count: 0,
      avgTotalReturn: 0,
      avgCAGR: 0,
      totalDividends: 0,
      avgDividendYield: null,
      avgVolatility: null,
      bestPerformer: null,
      worstPerformer: null,
    };
  }

  const count = tickers.length;

  // Calculate averages
  const avgTotalReturn =
    tickers.reduce((sum, t) => sum + t.totalReturn, 0) / count;
  const avgCAGR = tickers.reduce((sum, t) => sum + t.cagr, 0) / count;

  // Sum dividends
  const totalDividends = tickers.reduce(
    (sum, t) => sum + t.dividendsReceived,
    0
  );

  // Calculate average dividend yield (only for tickers that have it)
  const tickersWithYield = tickers.filter(
    (t) => t.dividendYield != null && t.dividendYield > 0
  );
  const avgDividendYield =
    tickersWithYield.length > 0
      ? tickersWithYield.reduce((sum, t) => sum + (t.dividendYield || 0), 0) /
        tickersWithYield.length
      : null;

  // Calculate average volatility (only for tickers that have it)
  const tickersWithVolatility = tickers.filter(
    (t) => t.volatilityPercent != null
  );
  const avgVolatility =
    tickersWithVolatility.length > 0
      ? tickersWithVolatility.reduce(
          (sum, t) => sum + (t.volatilityPercent || 0),
          0
        ) / tickersWithVolatility.length
      : null;

  // Find best and worst performers
  const sortedByCAGR = [...tickers].sort((a, b) => b.cagr - a.cagr);
  const bestPerformer = sortedByCAGR[0]?.ticker || null;
  const worstPerformer = sortedByCAGR[sortedByCAGR.length - 1]?.ticker || null;

  return {
    count,
    avgTotalReturn,
    avgCAGR,
    totalDividends,
    avgDividendYield,
    avgVolatility,
    bestPerformer,
    worstPerformer,
  };
}

/**
 * Sort groups by their aggregates
 */
function sortGroups(
  groups: GroupedTickerData<TickerResult>[],
  mode: GroupingMode
): GroupedTickerData<TickerResult>[] {
  // For performance mode, use a specific order
  if (mode === 'performance') {
    const order = ['HIGH', 'MEDIUM', 'LOW', 'NEGATIVE'];
    return [...groups].sort((a, b) => {
      return order.indexOf(a.groupKey) - order.indexOf(b.groupKey);
    });
  }

  // For dividend mode, put payers first
  if (mode === 'dividend') {
    return [...groups].sort((a, b) => {
      if (a.groupKey === 'Dividend Payers') return -1;
      if (b.groupKey === 'Dividend Payers') return 1;
      return 0;
    });
  }

  // For asset type, put EQUITY first
  if (mode === 'assetType') {
    return [...groups].sort((a, b) => {
      if (a.groupKey === 'EQUITY') return -1;
      if (b.groupKey === 'EQUITY') return 1;
      if (a.groupKey === 'Unknown') return 1;
      if (b.groupKey === 'Unknown') return -1;
      return a.groupKey.localeCompare(b.groupKey);
    });
  }

  // Default: sort by count (largest groups first), then alphabetically
  // Put "Unknown" and "No Underlying" groups at the end
  return [...groups].sort((a, b) => {
    const aIsUnknown =
      a.groupKey.startsWith('Unknown') || a.groupKey === 'No Underlying';
    const bIsUnknown =
      b.groupKey.startsWith('Unknown') || b.groupKey === 'No Underlying';

    if (aIsUnknown && !bIsUnknown) return 1;
    if (!aIsUnknown && bIsUnknown) return -1;

    // Sort by count descending, then alphabetically
    if (b.aggregates.count !== a.aggregates.count) {
      return b.aggregates.count - a.aggregates.count;
    }
    return a.groupKey.localeCompare(b.groupKey);
  });
}

export interface UseGroupedDataOptions {
  /** Sort order for tickers within each group */
  sortBy?: 'ticker' | 'cagr' | 'totalReturn' | 'dividends';
  /** Sort direction */
  sortDirection?: 'asc' | 'desc';
}

/**
 * Custom hook for grouping ticker data by various criteria
 *
 * @param data - Array of ticker results to group
 * @param groupingMode - How to group the data
 * @param options - Optional sorting configuration
 * @returns Array of grouped ticker data with aggregates
 */
export function useGroupedData(
  data: TickerResult[],
  groupingMode: GroupingMode,
  options: UseGroupedDataOptions = {}
): GroupedTickerData<TickerResult>[] {
  const { sortBy = 'cagr', sortDirection = 'desc' } = options;

  return useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    // Special case: ticker mode returns each ticker as its own group
    if (groupingMode === 'ticker') {
      return data.map((ticker) => ({
        groupKey: ticker.ticker,
        groupLabel: ticker.ticker,
        tickers: [ticker],
        aggregates: calculateAggregates([ticker]),
      }));
    }

    // Group tickers by the selected criteria
    const groupsMap = new Map<string, TickerResult[]>();

    for (const ticker of data) {
      const key = getGroupKey(ticker, groupingMode);
      const existing = groupsMap.get(key) || [];
      existing.push(ticker);
      groupsMap.set(key, existing);
    }

    // Convert map to array with aggregates
    const groups: GroupedTickerData<TickerResult>[] = [];

    for (const [key, tickers] of groupsMap) {
      // Sort tickers within the group
      const sortedTickers = [...tickers].sort((a, b) => {
        let comparison = 0;
        switch (sortBy) {
          case 'ticker':
            comparison = a.ticker.localeCompare(b.ticker);
            break;
          case 'cagr':
            comparison = a.cagr - b.cagr;
            break;
          case 'totalReturn':
            comparison = a.totalReturn - b.totalReturn;
            break;
          case 'dividends':
            comparison = a.dividendsReceived - b.dividendsReceived;
            break;
          default:
            comparison = 0;
        }
        return sortDirection === 'desc' ? -comparison : comparison;
      });

      groups.push({
        groupKey: key,
        groupLabel: getGroupLabel(key, groupingMode),
        tickers: sortedTickers,
        aggregates: calculateAggregates(tickers),
      });
    }

    // Sort groups
    return sortGroups(groups, groupingMode);
  }, [data, groupingMode, sortBy, sortDirection]);
}

export default useGroupedData;
